import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

/**
 * Componente para renderizar marcadores de veículos em tempo real no Google Maps
 * @param {Array} vehicles - Array de veículos com posições
 * @param {Object} map - Referência ao mapa Google Maps
 */
const VehicleMarkers = forwardRef(
  ({ vehicles = [], map, onVehicleClick }, ref) => {
    const markersRef = useRef(new Map());
    const animationsRef = useRef(new Map());
    const lastPositionsRef = useRef(new Map());
    const lastHeadingsRef = useRef(new Map());

    // Expor método para abrir InfoWindow de um veículo específico
    useImperativeHandle(ref, () => ({
      openInfoWindow: (vehicleId) => {
        const marker = markersRef.current.get(vehicleId);
        if (marker && marker.infoWindow) {
          // Fechar outros InfoWindows
          markersRef.current.forEach((m) => {
            if (m.infoWindow) m.infoWindow.close();
          });
          // Abrir InfoWindow do veículo selecionado
          marker.infoWindow.setContent(marker.infoWindowContent);
          marker.infoWindow.open(map, marker);
        }
      },
    }));

    useEffect(() => {
      if (!map || !window.google) return;

      const currentMarkers = markersRef.current;
      const currentAnimations = animationsRef.current;
      const lastPositions = lastPositionsRef.current;
      const lastHeadings = lastHeadingsRef.current;

      const toRad = (deg) => (deg * Math.PI) / 180;
      const toDeg = (rad) => (rad * 180) / Math.PI;

      const distanceMetersBetween = (a, b) => {
        // Haversine (suficiente e estável para distâncias pequenas)
        const R = 6371000;
        const dLat = toRad(b.lat - a.lat);
        const dLng = toRad(b.lng - a.lng);
        const lat1 = toRad(a.lat);
        const lat2 = toRad(b.lat);

        const s1 = Math.sin(dLat / 2);
        const s2 = Math.sin(dLng / 2);
        const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
        const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
        return R * c;
      };

      const bearingDegreesBetween = (from, to) => {
        // Bearing inicial: 0=Norte, 90=Leste
        const φ1 = toRad(from.lat);
        const φ2 = toRad(to.lat);
        const Δλ = toRad(to.lng - from.lng);

        const y = Math.sin(Δλ) * Math.cos(φ2);
        const x =
          Math.cos(φ1) * Math.sin(φ2) -
          Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
        const θ = Math.atan2(y, x);
        const deg = (toDeg(θ) + 360) % 360;
        return deg;
      };

      const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n);

      const resolveHeading = (vehicleId, lat, lng, feedBearing) => {
        // 1) Se o feed trouxer bearing válido, confia nele
        if (isFiniteNumber(feedBearing)) {
          lastHeadings.set(vehicleId, feedBearing);
          lastPositions.set(vehicleId, { lat, lng });
          return feedBearing;
        }

        // 2) Senão, calcula pela diferença de posição
        const prev = lastPositions.get(vehicleId);
        lastPositions.set(vehicleId, { lat, lng });

        if (!prev) {
          const fallback = lastHeadings.get(vehicleId) || 0;
          return fallback;
        }

        const dist = distanceMetersBetween(prev, { lat, lng });

        // Jitter: se não moveu o suficiente, mantém última direção
        if (dist < 8) {
          return lastHeadings.get(vehicleId) || 0;
        }

        // Teleporte/ruído: não recalcula direção em salto muito grande
        if (dist > 800) {
          return lastHeadings.get(vehicleId) || 0;
        }

        const computed = bearingDegreesBetween(prev, { lat, lng });
        lastHeadings.set(vehicleId, computed);
        return computed;
      };

      const BUS_MARKER_Z_INDEX = 5000;
      const BUS_ICON_SIZE_PX = 44;
      const BUS_ICON_ANCHOR = new window.google.maps.Point(
        Math.floor(BUS_ICON_SIZE_PX / 2),
        BUS_ICON_SIZE_PX - 2
      );

      const escapeHtml = (value) => {
        const str = value === null || value === undefined ? "" : String(value);
        return str
          .replaceAll("&", "&amp;")
          .replaceAll("<", "&lt;")
          .replaceAll(">", "&gt;")
          .replaceAll('"', "&quot;")
          .replaceAll("'", "&#39;");
      };

      const isValidHexColor = (value) => {
        if (typeof value !== "string") return false;
        return /^#[0-9A-Fa-f]{6}$/.test(value);
      };

      const getDirectionText = (vehicle) => {
        const label = vehicle?.directionLabel;
        const terminus = vehicle?.directionTerminus;

        if (label && terminus) return `${label} → ${terminus}`;
        if (label) return String(label);

        const numeric = Number(vehicle?.directionId);
        if (numeric === 0) return "IDA";
        if (numeric === 1) return "VOLTA";
        return "Sentido indefinido";
      };

      // Criar ícone SVG no estilo “pinpoint” na cor da linha
      const createBusPinIcon = (color) => {
        const c = color || "#4CAF50";

        // SVG desenhado manualmente (sem fontes externas) para evitar dependências.
        // O anchor do marcador fica na ponta inferior do pin.
        const svg = `
        <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#111827" flood-opacity="0.25" />
            </filter>
          </defs>

          <!-- Pin -->
          <path
            d="M18 2c-6.35 0-11.5 5.15-11.5 11.5C6.5 22.2 18 34 18 34s11.5-11.8 11.5-20.5C29.5 7.15 24.35 2 18 2z"
            fill="${c}"
            stroke="#ffffff"
            stroke-width="2"
            filter="url(#shadow)"
          />

          <!-- Área interna (onde fica o ícone) -->
          <circle cx="18" cy="14" r="8" fill="#ffffff" fill-opacity="0.95" />

          <!-- Ícone de ônibus (simples) -->
          <g transform="translate(18 14)">
            <rect x="-6.2" y="-5" width="12.4" height="10.2" rx="2" fill="${c}" />
            <rect x="-4.9" y="-3.8" width="9.8" height="3" rx="0.8" fill="#ffffff" fill-opacity="0.95" />
            <rect x="-4.9" y="0.1" width="9.8" height="2.6" rx="0.8" fill="#ffffff" fill-opacity="0.25" />

            <circle cx="-3.7" cy="5.1" r="1.2" fill="#111827" fill-opacity="0.85" />
            <circle cx="3.7" cy="5.1" r="1.2" fill="#111827" fill-opacity="0.85" />
            <circle cx="-3.7" cy="5.1" r="0.5" fill="#ffffff" fill-opacity="0.9" />
            <circle cx="3.7" cy="5.1" r="0.5" fill="#ffffff" fill-opacity="0.9" />
          </g>
        </svg>
      `;

        // Evita btoa() (quebra com unicode em alguns WebViews/browsers)
        const encoded = encodeURIComponent(svg)
          .replace(/%0A/g, "")
          .replace(/%0D/g, "");
        return `data:image/svg+xml;charset=UTF-8,${encoded}`;
      };

      const cancelAnimation = (vehicleId) => {
        const existing = animationsRef.current.get(vehicleId);
        if (!existing) return;
        cancelAnimationFrame(existing.rafId);
        animationsRef.current.delete(vehicleId);
      };

      const animateMarkerPosition = (
        vehicleId,
        marker,
        fromPos,
        toPos,
        durationMs = 900
      ) => {
        cancelAnimation(vehicleId);

        const start = performance.now();
        const fromLat = fromPos.lat();
        const fromLng = fromPos.lng();
        const toLat = toPos.lat;
        const toLng = toPos.lng;

        const step = (now) => {
          const tRaw = (now - start) / durationMs;
          const t = Math.min(1, Math.max(0, tRaw));

          // easeOutCubic
          const eased = 1 - Math.pow(1 - t, 3);

          const lat = fromLat + (toLat - fromLat) * eased;
          const lng = fromLng + (toLng - fromLng) * eased;

          marker.setPosition({ lat, lng });

          if (t < 1) {
            const rafId = requestAnimationFrame(step);
            animationsRef.current.set(vehicleId, { rafId });
            return;
          }

          animationsRef.current.delete(vehicleId);
        };

        const rafId = requestAnimationFrame(step);
        animationsRef.current.set(vehicleId, { rafId });
      };

      // Atualizar/criar marcadores
      vehicles.forEach((vehicle) => {
        const existingMarker = currentMarkers.get(vehicle.vehicleId);
        const position = { lat: vehicle.lat, lng: vehicle.lng };

        const vehicleDisplayId =
          vehicle.vehiclePrefix ?? vehicle.vehicleId ?? vehicle.plate ?? null;

        // Normalizar cor: adicionar # se necessário, usar cinza para veículos sem linha
        let color = "#4CAF50"; // Verde padrão
        if (vehicle.routeColor && vehicle.routeColor !== "#000000") {
          const candidate = vehicle.routeColor.startsWith("#")
            ? vehicle.routeColor
            : `#${vehicle.routeColor}`;
          color = isValidHexColor(candidate) ? candidate : "#4CAF50";
        } else if (!vehicle.routeShortName) {
          color = "#9CA3AF"; // Cinza para veículos sem linha
        }

        if (existingMarker) {
          // Atualizar posição do marcador existente com suavização
          const currentPos = existingMarker.getPosition();
          if (!currentPos) {
            existingMarker.setPosition(position);
          } else {
            animateMarkerPosition(
              vehicle.vehicleId,
              existingMarker,
              currentPos,
              position
            );
          }

          // Atualizar ícone (pinpoint). Ainda calculamos heading para manter histórico,
          // mas o ícone não gira (visual mais “pin de mapa”).
          resolveHeading(
            vehicle.vehicleId,
            vehicle.lat,
            vehicle.lng,
            vehicle.bearing
          );
          existingMarker.setIcon({
            url: createBusPinIcon(color),
            scaledSize: new window.google.maps.Size(BUS_ICON_SIZE_PX, BUS_ICON_SIZE_PX),
            anchor: BUS_ICON_ANCHOR,
          });

          // Garantir que o ônibus fique sempre acima das paradas
          existingMarker.setZIndex(BUS_MARKER_Z_INDEX);

          // Atualizar conteúdo do InfoWindow
          const directionText = getDirectionText(vehicle);
          existingMarker.infoWindowContent = `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: bold; font-size: 16px; color: ${color}; margin-bottom: 8px;">
              🚌 ${
                vehicle.routeShortName
                  ? `Linha ${escapeHtml(vehicle.routeShortName)}`
                  : "Veículo sem linha"
              }
            </div>
            <div style="margin-bottom: 4px;">
              ${escapeHtml(vehicle.routeLongName || "Informação não disponível")}
            </div>
            <div style="font-size: 12px; color: #334155; margin-top: 6px;">
              🧭 ${escapeHtml(directionText)}
            </div>
            <div style="font-size: 12px; color: #666; margin-top: 8px;">
              ${vehicleDisplayId ? `Prefixo: ${escapeHtml(vehicleDisplayId)}` : ""}
            </div>
          </div>
        `;

          // Se o InfoWindow estiver aberto, atualiza o conteúdo sem fechar/reabrir
          // para evitar o efeito de "piscar".
          if (
            existingMarker.infoWindow &&
            existingMarker.infoWindow.getMap &&
            existingMarker.infoWindow.getMap()
          ) {
            existingMarker.infoWindow.setContent(
              existingMarker.infoWindowContent
            );
          }
        } else {
          // Criar novo marcador
          resolveHeading(vehicle.vehicleId, vehicle.lat, vehicle.lng, vehicle.bearing);
          const directionText = getDirectionText(vehicle);
          const marker = new window.google.maps.Marker({
            position,
            map,
            icon: {
              url: createBusPinIcon(color),
              scaledSize: new window.google.maps.Size(BUS_ICON_SIZE_PX, BUS_ICON_SIZE_PX),
              anchor: BUS_ICON_ANCHOR,
            },
            title: `Linha ${vehicle.routeShortName || vehicle.routeId}`,
            zIndex: BUS_MARKER_Z_INDEX, // Garantir que fique acima de paradas/overlays
          });

          // Criar InfoWindow
          marker.infoWindowContent = `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: bold; font-size: 16px; color: ${color}; margin-bottom: 8px;">
              🚌 ${
                vehicle.routeShortName
                  ? `Linha ${escapeHtml(vehicle.routeShortName)}`
                  : "Veículo sem linha"
              }
            </div>
            <div style="margin-bottom: 4px;">
              ${escapeHtml(vehicle.routeLongName || "Informação não disponível")}
            </div>
            <div style="font-size: 12px; color: #334155; margin-top: 6px;">
              🧭 ${escapeHtml(directionText)}
            </div>
            <div style="font-size: 12px; color: #666; margin-top: 8px;">
              ${vehicleDisplayId ? `Prefixo: ${escapeHtml(vehicleDisplayId)}` : ""}
            </div>
          </div>
        `;

          const infoWindow = new window.google.maps.InfoWindow();
          marker.infoWindow = infoWindow;

          /*
          // Temporariamente desabilitado: não abrir InfoWindow ao clicar no marcador
          marker.addListener("click", () => {
            // Fechar outros InfoWindows
            currentMarkers.forEach((m) => {
              if (m.infoWindow) m.infoWindow.close();
            });

            infoWindow.setContent(marker.infoWindowContent);
            infoWindow.open(map, marker);

            if (typeof onVehicleClick === "function") {
              onVehicleClick(vehicle);
            }
          });
          */

          currentMarkers.set(vehicle.vehicleId, marker);
        }
      });

      // Remover marcadores de veículos que não existem mais
      const currentVehicleIds = new Set(vehicles.map((v) => v.vehicleId));
      for (const [vehicleId, marker] of currentMarkers.entries()) {
        if (!currentVehicleIds.has(vehicleId)) {
          cancelAnimation(vehicleId);
          marker.setMap(null);
          if (marker.infoWindow) marker.infoWindow.close();
          currentMarkers.delete(vehicleId);

          lastPositions.delete(vehicleId);
          lastHeadings.delete(vehicleId);
        }
      }

      // Cleanup ao desmontar
      return () => {
        // Cancelar animações pendentes
        currentAnimations.forEach(({ rafId }) => cancelAnimationFrame(rafId));
        currentAnimations.clear();

        currentMarkers.forEach((marker) => {
          marker.setMap(null);
          if (marker.infoWindow) marker.infoWindow.close();
        });
        currentMarkers.clear();
      };
    }, [vehicles, map, onVehicleClick]);

    return null; // Não renderiza nada visualmente
  }
);

VehicleMarkers.displayName = "VehicleMarkers";

export default VehicleMarkers;
